/**
 * Offline Indicator Component
 *
 * Displays a banner when the user is offline and shows sync status
 * for pending offline expenses.
 */
import {
  Check,
  ChevronDown,
  ChevronUp,
  CloudOff,
  RefreshCw,
  TriangleAlert,
  X,
} from 'lucide-react';
import { Link } from 'react-router-dom';

import { useEffect, useRef, useState } from 'react';

import {
  SYNC_GROUP_GONE_ERROR,
  SYNC_GROUP_LIMIT_ERROR,
  SYNC_LINE_ITEMS_NOT_ALLOWED_ERROR,
  SYNC_RATE_UNAVAILABLE_ERROR,
  SYNC_REPEAT_PRO_REQUIRED_ERROR,
  formatCurrency,
  formatDate,
} from '@splitfiesta/core';
import { useTranslation } from '@splitfiesta/i18n';
import type { PendingExpense } from '@splitfiesta/shared';

import { ConfirmationDialog } from '@/components/common/ConfirmationDialog';

import { useDeleteExpenseMutation } from '@/hooks/queries/useExpenses';
import { useNetworkStatus } from '@/hooks/useNetworkStatus';

import { cn } from '@/lib/utils';

import {
  useFailedExpenses,
  useIsSyncing,
  useOfflineOwnerId,
  useOfflineStore,
} from '@/stores/offlineStore';

function FailedExpenseList({
  expenses,
  onDiscard,
}: {
  expenses: PendingExpense[];
  onDiscard: (expense: PendingExpense) => void;
}) {
  const { t, i18n } = useTranslation('common');
  // Keys in `lastError` carry their own namespace; this loads it.
  const { t: tExpenses } = useTranslation('expenses');

  return (
    <ul className="mt-1 max-h-40 w-full max-w-md space-y-1 overflow-y-auto text-left text-xs">
      {expenses.map(expense => {
        const isRateError = expense.lastError === SYNC_RATE_UNAVAILABLE_ERROR;
        const isGroupGone = expense.lastError === SYNC_GROUP_GONE_ERROR;
        const isGroupLimit = expense.lastError === SYNC_GROUP_LIMIT_ERROR;
        const isItemsNotAllowed =
          expense.lastError === SYNC_LINE_ITEMS_NOT_ALLOWED_ERROR;
        const isRepeatProRequired =
          expense.lastError === SYNC_REPEAT_PRO_REQUIRED_ERROR;
        const reason = isRateError
          ? tExpenses(SYNC_RATE_UNAVAILABLE_ERROR, {
              currency: expense.currency,
              date: formatDate(expense.date, i18n.language),
            })
          : isGroupGone
            ? t('offline.groupGone')
            : isGroupLimit
              ? t('offline.groupLimitReached')
              : isItemsNotAllowed
                ? tExpenses(SYNC_LINE_ITEMS_NOT_ALLOWED_ERROR)
                : isRepeatProRequired
                  ? t(SYNC_REPEAT_PRO_REQUIRED_ERROR)
                  : t('offline.syncFailedGeneric');
        return (
          <li
            key={expense.localId}
            className="flex items-start gap-3 rounded-md bg-white/10 px-2 py-1.5"
          >
            <div className="min-w-0 flex-1">
              <div className="flex items-baseline justify-between gap-2">
                <span className="truncate text-white/80">
                  {expense.title || expense.description}
                </span>
                <span className="shrink-0 font-semibold text-white">
                  {formatCurrency(expense.amount, expense.currency)}
                </span>
              </div>
              <p className="text-white/70">{reason}</p>
            </div>
            <div className="flex shrink-0 items-center gap-3 self-center">
              {/* No edit can bring a group back. */}
              {!isGroupGone && (
                <Link
                  to={`/groups/${expense.groupId}/expenses/${expense.localId}/edit`}
                  className="font-medium text-white/90 underline underline-offset-2 hover:text-white"
                >
                  {isRateError
                    ? tExpenses('conversion.editRate')
                    : isItemsNotAllowed
                      ? tExpenses('lineItems.changeSplit')
                      : t('actions.edit')}
                </Link>
              )}
              <button
                type="button"
                onClick={() => onDiscard(expense)}
                className="font-medium text-white/90 underline underline-offset-2 hover:text-white"
              >
                {t('offline.discardQueued')}
              </button>
            </div>
          </li>
        );
      })}
    </ul>
  );
}

export function OfflineIndicator() {
  const { t } = useTranslation('common');
  const { t: tExpenses } = useTranslation('expenses');
  const { isOnline } = useNetworkStatus();
  const lastSyncWarning = useOfflineStore(state => state.lastSyncWarning);
  const clearSyncWarning = useOfflineStore(state => state.clearSyncWarning);
  const failedExpenses = useFailedExpenses();
  const failedCount = failedExpenses.length;
  const isSyncing = useIsSyncing();
  const isSignedIn = useOfflineOwnerId() !== undefined;
  const [showSuccess, setShowSuccess] = useState(false);
  const wasSyncingRef = useRef(false);
  // The banner is fixed over the nav; the per-item list only unfolds on
  // request so it never covers the page by default.
  const [listOpen, setListOpen] = useState(false);
  const [toDiscard, setToDiscard] = useState<PendingExpense | null>(null);
  const deleteExpense = useDeleteExpenseMutation();
  // A sync may take the row mid-confirm; discarding it then would leave the
  // copy that sync is creating on the server.
  if (isSyncing && toDiscard) setToDiscard(null);
  const confirmDiscard = () => {
    if (toDiscard) {
      deleteExpense.mutate({
        expenseId: toDiscard.localId,
        groupId: toDiscard.groupId,
      });
    }
    setToDiscard(null);
  };

  // Track when sync completes to show success message briefly
  useEffect(() => {
    if (isSyncing) {
      wasSyncingRef.current = true;
      return;
    }

    if (wasSyncingRef.current && failedCount === 0) {
      // Sync just completed successfully - use timeouts to avoid sync setState in effect
      wasSyncingRef.current = false;
      const showTimer = setTimeout(() => setShowSuccess(true), 0);
      const hideTimer = setTimeout(() => setShowSuccess(false), 2000);
      return () => {
        clearTimeout(showTimer);
        clearTimeout(hideTimer);
      };
    }
  }, [isSyncing, failedCount]);

  // Don't show when offline - the HomePage handles offline UI
  // Show when: syncing, has failures, or showing success message
  if (
    !isSignedIn ||
    !isOnline ||
    (!isSyncing && failedCount === 0 && !showSuccess && !lastSyncWarning)
  ) {
    return null;
  }

  // A synced row whose receipt draft had expired: saved without the photo.
  const warningLine = lastSyncWarning ? (
    <div className="flex items-center justify-center gap-2">
      <TriangleAlert className="h-4 w-4 shrink-0" aria-hidden="true" />
      <span>{tExpenses('scan.receiptDropped')}</span>
      <button
        type="button"
        onClick={clearSyncWarning}
        aria-label={t('actions.close')}
        className="ml-1 inline-flex min-h-11 min-w-11 items-center justify-center rounded hover:bg-white/15 focus:outline-none focus-visible:ring-2 focus-visible:ring-white"
      >
        <X className="h-4 w-4" aria-hidden="true" />
      </button>
    </div>
  ) : null;

  if (!isSyncing && failedCount === 0 && !showSuccess) {
    return (
      <div
        role="status"
        className="fixed top-0 left-0 right-0 z-50 min-h-16 px-4 py-2 text-center text-sm font-medium flex flex-col items-center justify-center bg-warning-700 text-white"
      >
        {warningLine}
      </div>
    );
  }

  return (
    <div
      className={cn(
        'fixed top-0 left-0 right-0 z-50 min-h-16 px-4 py-2 text-center text-sm font-medium transition-all duration-300 flex flex-col items-center justify-center',
        isSyncing
          ? 'bg-blue-500 text-white'
          : failedCount > 0
            ? 'bg-red-500 text-white'
            : 'bg-green-500 text-white'
      )}
    >
      <div className="flex items-center justify-center gap-2">
        {isSyncing ? (
          <>
            <RefreshCw className="h-4 w-4 animate-spin" />
            <span>{t('offline.syncing')}</span>
          </>
        ) : failedCount > 0 ? (
          <>
            <CloudOff className="h-4 w-4" />
            <span>{t('offline.syncFailed', { count: failedCount })}</span>
            <button
              type="button"
              onClick={() => setListOpen(open => !open)}
              aria-expanded={listOpen}
              aria-label={
                listOpen ? t('actions.close') : t('actions.viewDetails')
              }
              className="ml-1 rounded p-0.5 hover:bg-white/15"
            >
              {listOpen ? (
                <ChevronUp className="h-4 w-4" />
              ) : (
                <ChevronDown className="h-4 w-4" />
              )}
            </button>
          </>
        ) : (
          <>
            <Check className="h-4 w-4" />
            <span>{t('offline.syncComplete')}</span>
          </>
        )}
      </div>
      {warningLine}
      {!isSyncing && listOpen && failedExpenses.length > 0 && (
        <FailedExpenseList expenses={failedExpenses} onDiscard={setToDiscard} />
      )}
      <ConfirmationDialog
        isOpen={toDiscard !== null}
        onClose={() => setToDiscard(null)}
        onConfirm={confirmDiscard}
        title={t('offline.discardQueuedTitle')}
        description={t('offline.discardQueuedConfirm')}
        confirmText={t('offline.discardQueued')}
        cancelText={t('actions.cancel')}
        variant="danger"
      />
    </div>
  );
}
